import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { buildApiUrl } from "@/lib/config";
import Seo from "@/components/Seo";
import {
  buildBreadcrumbJsonLd,
  buildCanonicalUrl,
  buildWebPageJsonLd,
  buildProductJsonLd,
  buildTouristTripJsonLd,
} from "@/lib/seo";
import { analyticsEvents, trackEvent } from "@/lib/ga4";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Star,
  Check,
  Phone,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import emailjs from "@emailjs/browser";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

interface Package {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  destination: string;
  type: string;
  image: string;
  imageUrl: string;
  featured: boolean;
  highlights: string[];
  includes: string[];
}

const toSlug = (value?: string) =>
  value
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function PackageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [requestType, setRequestType] = useState<"book" | "quote">("book");

  const {
    data: pkg,
    isLoading,
    error,
  } = useQuery<Package>({
    queryKey: ["/api/packages", id],
    queryFn: async () => {
      const response = await fetch(buildApiUrl(`api/packages/${id}`));
      if (!response.ok) {
        throw new Error("Package not found");
      }
      return response.json();
    },
    enabled: !!id,
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const emailConfig = {
    serviceId: "service_4pz4dzf",
    templateId: "template_aelz1vr",
    publicKey: "uhHa5ey8ZNE5hSWsN",
    toEmails: [
      "it@travelwingsonline.in",
      "support@travelwingsonline.in",
      "hr@travelwingsonline.in",
    ],
  };

  const bookingSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    phone: z.string().min(1, "Phone number is required"),
  });

  type BookingFormData = z.infer<typeof bookingSchema>;

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  const bookingMutation = useMutation({
    mutationFn: async (data: BookingFormData) => {
      if (!pkg) {
        throw new Error("Package is not available");
      }

      const messageBody = [
        "Hello Team,",
        "",
        "A customer has shown interest in one of our packages through the website. Below are the complete details of the request:",
        "",
        `Name: ${data.firstName} ${data.lastName}`,
        `Email: ${data.email}`,
        `Phone: ${data.phone}`,
        `Service Interest: ${
          requestType === "book" ? "Book Now" : "Request Quote"
        }`,
        "",
        "Message:",
        `Package: ${pkg.title}`,
        `Package ID: ${pkg.id}`,
        `Price: ${formatPrice(pkg.price)}`,
        `Duration: ${pkg.duration}`,
        `Destination: ${pkg.destination || "N/A"}`,
        "",
        "Source: Package Booking Form",
      ].join("\n");

      const emailParams = {
        first_name: data.firstName,
        last_name: data.lastName,
        reply_to: data.email,
        user_email: data.email,
        phone: data.phone,
        service_interest: requestType === "book" ? "Book Now" : "Request Quote",
        message: messageBody,
        to_email: emailConfig.toEmails.join(","),
      };

      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        emailParams,
        { publicKey: emailConfig.publicKey }
      );

      return { status: "email_sent" };
    },
    onSuccess: () => {
      trackEvent({
        name: analyticsEvents.generateLead,
        params: {
          content_type: "lead",
          item_id: pkg?.id,
          item_name: pkg?.title,
          page_type: "package_detail",
          page_path: `/packages/${id}`,
          lead_type: "package_booking",
          package_id: pkg?.id,
          package_name: pkg?.title,
          intent: requestType,
          result: "success",
        },
      });
      toast({
        title: "Request sent successfully!",
        description:
          requestType === "book"
            ? "We have received your booking request."
            : "We have received your quote request.",
      });
      form.reset();
      setIsDialogOpen(false);
    },
    onError: (mutationError: Error) => {
      trackEvent({
        name: analyticsEvents.generateLead,
        params: {
          content_type: "lead",
          item_id: pkg?.id,
          item_name: pkg?.title,
          page_type: "package_detail",
          page_path: `/packages/${id}`,
          lead_type: "package_booking",
          package_id: pkg?.id,
          package_name: pkg?.title,
          intent: requestType,
          result: "error",
          message: mutationError.message,
        },
      });
      toast({
        title: "Failed to send request",
        description: mutationError.message,
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (!pkg) return;
    trackEvent({
      name: analyticsEvents.viewPackage,
      params: {
        content_type: "package",
        item_id: pkg.id,
        item_name: pkg.title,
        package_id: pkg.id,
        package_name: pkg.title,
        price_inr: pkg.price,
        duration: pkg.duration,
        destination: pkg.destination,
        page_path: `/packages/${id}`,
        page_type: "package_detail",
      },
    });
  }, [pkg, id]);

  useEffect(() => {
    if (!pkg?.destination) return;
    trackEvent({
      name: analyticsEvents.viewDestination,
      params: {
        content_type: "destination",
        item_id: toSlug(pkg.destination),
        item_name: pkg.destination,
        package_id: pkg.id,
        page_path: `/packages/${id}`,
        page_type: "package_detail",
      },
    });
  }, [pkg, id]);

  const handleOpenDialog = (type: "book" | "quote") => {
    setRequestType(type);
    trackEvent({
      name: analyticsEvents.ctaClick,
      params: {
        content_type: "cta",
        item_id: type,
        item_name: type,
        cta_label: type === "book" ? "book_now" : "request_quote",
        package_id: pkg?.id,
        package_name: pkg?.title,
        page_path: `/packages/${id}`,
        page_type: "package_detail",
      },
    });
    setIsDialogOpen(true);
  };

  const onSubmit = (data: BookingFormData) => {
    bookingMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-50">
        <Seo
          title="Loading package | Travel Wings Global"
          description="View detailed travel packages from Travel Wings Global."
          path={`/packages/${id || ""}`}
        />
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-50">
        <Seo
          title="Package not found | Travel Wings Global"
          description="The travel package you are looking for is unavailable."
          path={`/packages/${id || ""}`}
          noindex
        />
        <Navigation />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Package Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The package you're looking for doesn't exist.
          </p>
          <Button
            onClick={() => setLocation("/packages")}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Packages
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const canonicalUrl = buildCanonicalUrl(`/packages/${id}`);
  const packageImage = pkg.imageUrl || pkg.image;
  const breadcrumbs = buildBreadcrumbJsonLd([
    { name: "Home", url: buildCanonicalUrl("/") },
    { name: "Packages", url: buildCanonicalUrl("/packages") },
    { name: pkg.title, url: canonicalUrl },
  ]);
  const webPageJsonLd = buildWebPageJsonLd({
    name: `${pkg.title} | Travel Wings Global Packages`,
    description: pkg.description,
    url: canonicalUrl,
    image: packageImage,
    type: "ItemPage",
  });
  const touristTripJsonLd = buildTouristTripJsonLd({
    name: pkg.title,
    description: pkg.description,
    url: canonicalUrl,
    image: packageImage,
    destination: pkg.destination,
    tripType: pkg.type,
    highlights: pkg.highlights,
    price: pkg.price,
    priceCurrency: "INR",
  });
  const productJsonLd = buildProductJsonLd({
    name: pkg.title,
    description: pkg.description,
    url: canonicalUrl,
    image: packageImage,
    price: pkg.price,
    priceCurrency: "INR",
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-50">
      <Seo
        title={`${pkg.title} | Travel Wings Global Packages`}
        description={pkg.description}
        path={`/packages/${id}`}
        image={packageImage}
        structuredData={[
          webPageJsonLd,
          breadcrumbs,
          touristTripJsonLd,
          productJsonLd,
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <img
          src={pkg.imageUrl || pkg.image}
          alt={pkg.title}
          width={1920}
          height={384}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sizes="100vw"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <div className="flex items-center justify-center mb-4">
              {pkg.featured && (
                <Badge className="bg-orange-600 text-white mr-3">
                  <Star className="w-3 h-3 mr-1" />
                  Featured
                </Badge>
              )}
              <Badge variant="secondary" className="capitalize">
                {pkg.type}
              </Badge>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">{pkg.title}</h1>
            <div className="flex items-center justify-center space-x-6 text-lg">
              {pkg.destination && (
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  {pkg.destination}
                </div>
              )}
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                {pkg.duration}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => setLocation("/packages")}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Packages
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  About This Package
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {pkg.description}
                </p>
              </CardContent>
            </Card>

            {/* Highlights */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Package Highlights
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pkg.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start">
                      <Star className="w-5 h-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{highlight}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* What's Included */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  What's Included
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pkg.includes.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price & Booking */}
            <Card className="sticky top-4">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    {formatPrice(pkg.price)}
                  </div>
                  <p className="text-gray-600">per person</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-semibold">{pkg.duration}</span>
                  </div>
                  {pkg.destination && (
                    <div className="flex items-center justify-between py-3 border-b border-gray-200">
                      <span className="text-gray-600">Destination</span>
                      <span className="font-semibold">{pkg.destination}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-3">
                    <span className="text-gray-600">Type</span>
                    <Badge variant="secondary" className="capitalize">
                      {pkg.type}
                    </Badge>
                  </div>
                </div>

                <Button
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white mb-4"
                  onClick={() => handleOpenDialog("book")}
                >
                  Book Now
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleOpenDialog("quote")}
                >
                  Request Quote
                </Button>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Need Help?
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-orange-600 mr-3" />
                    <div>
                      <p className="font-semibold">Call Us</p>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>+91 88826 40963</p>
                        <p>+91 98108 52274</p>
                        <p>Vasant Kunj: +91 9582700184</p>
                        <p>+91 95827 00912</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-orange-600 mr-3" />
                    <div>
                      <p className="font-semibold">Email Us</p>
                      <p className="text-sm text-gray-600">
                        support@travelwingsonline.in
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {requestType === "book" ? "Book This Package" : "Request a Quote"}
            </DialogTitle>
            <DialogDescription>
              Enter your details and we'll reach out shortly.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-700">
            <div className="font-semibold text-gray-900">{pkg?.title}</div>
            <div className="flex flex-wrap gap-3 text-gray-600 mt-1">
              <span>Duration: {pkg?.duration}</span>
              {pkg?.destination && <span>Destination: {pkg.destination}</span>}
              <span>Price: {pkg ? formatPrice(pkg.price) : "-"}</span>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input placeholder="+91 98765 43210" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={bookingMutation.isPending}
                >
                  {bookingMutation.isPending ? "Sending..." : "Submit"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
